import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "iti_template_cart_v1";

/**
 * lineKey deduplicates cart lines that share the same product AND the same
 * variant selection. The variant is generic (`{ size: "M", color: "Black" }`,
 * `{ roast: "Light", grind: "Espresso" }`, …), so we hash by stable key=value
 * pairs sorted alphabetically.
 */
const lineKey = (item) => {
  const v = item.variant || {};
  const variantStr = Object.keys(v)
    .filter((k) => v[k] !== undefined && v[k] !== null && v[k] !== "")
    .sort()
    .map((k) => `${k}=${v[k]}`)
    .join("&");
  return `${item.product_id}::${variantStr}`;
};

/**
 * Cart provider for the standard ecommerce template. Pricing comes from the
 * active template config (free-shipping threshold + flat shipping). Wrap the
 * provider INSIDE `<TemplateProvider>` so `pricing` is correct per preset.
 */
export const CartProvider = ({ pricing, children }) => {
  const [items, setItems] = useState([]);
  const [hydrated, setHydrated] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch {/* ignore */}
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {/* ignore */}
  }, [items, hydrated]);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      const key = lineKey(item);
      const idx = prev.findIndex((p) => lineKey(p) === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + (item.quantity || 1) };
        return next;
      }
      return [...prev, { ...item, quantity: item.quantity || 1 }];
    });
    setDrawerOpen(true);
  }, []);

  const updateQty = useCallback((key, qty) => {
    setItems((prev) => prev.map((p) => (lineKey(p) === key ? { ...p, quantity: Math.max(1, qty) } : p)));
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((p) => lineKey(p) !== key));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const { subtotal, count } = useMemo(() => {
    const subtotal = items.reduce((s, it) => s + (it.price || 0) * it.quantity, 0);
    const count = items.reduce((s, it) => s + it.quantity, 0);
    return { subtotal, count };
  }, [items]);

  const freeOver = pricing?.freeShippingThreshold ?? 100;
  const flat = pricing?.flatShipping ?? 9.99;
  const shipping = items.length === 0 ? 0 : subtotal >= freeOver ? 0 : flat;
  const total = subtotal + shipping;

  return (
    <CartContext.Provider
      value={{ items, addItem, updateQty, removeItem, clear, subtotal, shipping, total, count, drawerOpen, setDrawerOpen, lineKey }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
